import React from 'react';
import { Select } from 'antd';
import dayjs from 'dayjs';
import {
  ClockCircleOutlined,
  TeamOutlined,
  FieldTimeOutlined,
  FileTextOutlined,
  SafetyCertificateOutlined,
  BankOutlined,
  AuditOutlined,
  EnvironmentOutlined,
  UserOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import SourceBadge from './SourceBadge';
import './aiCrew.css';

/**
 * Format a date string strictly as 'ddd DD MMM' (e.g. 'Mon 15 Sep')
 */
function formatDisplayDate(dateStr) {
  if (!dateStr) return '—';
  const d = dayjs(dateStr);
  return d.isValid() ? d.format('ddd DD MMM') : dateStr;
}

/**
 * ProposalHeader - Displays resolved shift parameters in an executive summary hero card,
 * labor context dimensions with icons, and transparent source badges.
 */
export default function ProposalHeader({
  proposal,
  originalPrompt,
  crewLeadId,
  onCrewLeadChange,
}) {
  if (!proposal) return null;

  const crew = proposal.crew || {};
  const sources = proposal.sources || {};
  const candidates = proposal.candidates || [];

  const eventTitle = crew.event?.label || crew.event?.code || crew.name || 'Event';
  const taskLabel = crew.task?.code && crew.task?.label && crew.task.label !== crew.task.code
    ? `${crew.task.label} (${crew.task.code})`
    : (crew.task?.label || crew.task?.code || 'Task');
  const dateFormatted = formatDisplayDate(crew.date);
  const endDateFormatted = crew.endDate ? formatDisplayDate(crew.endDate) : '';

  // Build lead options: ensure current proposal lead is always included
  const leadOptionsMap = new Map();
  if (crew.crewLeadId) {
    leadOptionsMap.set(crew.crewLeadId, {
      value: crew.crewLeadId,
      label: crew.crewLeadName ? `${crew.crewLeadName} (Lead)` : crew.crewLeadId,
    });
  }
  candidates.forEach((cand) => {
    if (cand.employeeId && !leadOptionsMap.has(cand.employeeId)) {
      leadOptionsMap.set(cand.employeeId, {
        value: cand.employeeId,
        label: `${cand.name} (${cand.trade || 'Worker'})`,
      });
    }
  });
  const leadOptions = Array.from(leadOptionsMap.values());

  const laborFields = [
    {
      key: 'task',
      label: 'Task',
      icon: <FileTextOutlined style={{ color: '#64748b', fontSize: 13 }} />,
      value: crew.task ? (crew.task.code ? `${crew.task.code} ${crew.task.label || ''}` : crew.task.label) : null,
      source: sources.task,
    },
    {
      key: 'premium',
      label: 'Premium',
      icon: <SafetyCertificateOutlined style={{ color: '#64748b', fontSize: 13 }} />,
      value: crew.premium,
      source: sources.premium,
    },
    {
      key: 'payScale',
      label: 'Pay scale',
      icon: <BankOutlined style={{ color: '#64748b', fontSize: 13 }} />,
      value: crew.payScale,
      source: sources.payScale,
    },
    {
      key: 'workRuleTransfer',
      label: 'Work rule',
      icon: <AuditOutlined style={{ color: '#64748b', fontSize: 13 }} />,
      value: crew.workRuleTransfer,
      source: sources.workRuleTransfer,
    },
    {
      key: 'venue',
      label: 'Venue',
      icon: <EnvironmentOutlined style={{ color: '#64748b', fontSize: 13 }} />,
      value: crew.venue,
      source: sources.venue,
    },
    {
      key: 'supervisor',
      label: 'Supervisor',
      icon: <UserOutlined style={{ color: '#64748b', fontSize: 13 }} />,
      value: crew.supervisor,
      source: sources.supervisor,
    },
  ];

  return (
    <div className="ai-crew-proposal-header" data-testid="ai-crew-proposal-header">
      {originalPrompt && (
        <div className="ai-crew-original-prompt">
          <span className="ai-crew-original-prompt-label">Original request:</span>
          <span className="ai-crew-original-prompt-text">&ldquo;{originalPrompt}&rdquo;</span>
        </div>
      )}

      {/* 1. Executive Shift Summary Hero Card */}
      <div className="ai-crew-hero-card">
        <div className={`ai-crew-hero-crewname ${!crew.name ? 'unset' : ''}`}>
          {crew.name || 'Crew name not set'}
        </div>

        <div className="ai-crew-hero-top">
          <div className="ai-crew-hero-event-info">
            <span className="ai-crew-hero-event-name">{eventTitle}</span>
            <span className="ai-crew-hero-task-tag">{taskLabel}</span>
            <span className="ai-crew-hero-date-tag">{dateFormatted}</span>
          </div>
        </div>

        {/* Shift Metrics Strip */}
        <div className="ai-crew-metrics-strip">
          <div className="ai-crew-metric-item">
            <span className="ai-crew-metric-label">
              <ClockCircleOutlined style={{ marginRight: 4 }} /> Shift Time
            </span>
            <span className="ai-crew-metric-value">
              {crew.start || '--:--'} &rarr; {crew.end || '--:--'}
              {crew.crossesMidnight && (
                <span className="ai-crew-overnight-pill">
                  ends {endDateFormatted || 'next day'}
                </span>
              )}
            </span>
          </div>

          <div className="ai-crew-metric-item">
            <span className="ai-crew-metric-label">
              <FieldTimeOutlined style={{ marginRight: 4 }} /> Duration
            </span>
            <span className="ai-crew-metric-value">
              {crew.durationHours ? `${crew.durationHours} hrs` : '—'}
            </span>
          </div>

          <div className="ai-crew-metric-item">
            <span className="ai-crew-metric-label">
              <TeamOutlined style={{ marginRight: 4 }} /> Target Headcount
            </span>
            <span className="ai-crew-metric-value">
              {crew.headcount || 0} workers
            </span>
          </div>
        </div>

        {/* Crew Lead Selection Bar */}
        <div className="ai-crew-lead-bar">
          <div className="ai-crew-lead-left">
            <span className="ai-crew-lead-label">Designated Crew Lead:</span>
            <Select
              value={crewLeadId || crew.crewLeadId || undefined}
              onChange={(val) => onCrewLeadChange && onCrewLeadChange(val)}
              options={leadOptions}
              placeholder="Select crew lead"
              size="middle"
              style={{ minWidth: 260 }}
            />
          </div>
          {sources.crewLeadId && (
            <div>
              <SourceBadge source={sources.crewLeadId} />
            </div>
          )}
        </div>
      </div>

      {/* 2. Optional Agent Summary / Rationale Banner */}
      {proposal.summary && (
        <div className="ai-crew-agent-rationale-box">
          <ThunderboltOutlined className="ai-crew-rationale-icon" />
          <div>
            <strong style={{ color: '#0f172a', marginRight: 6 }}>Agent Rationale:</strong>
            {proposal.summary}
          </div>
        </div>
      )}

      {/* 3. Labor Details Dimension Grid */}
      <div className="ai-crew-labor-section">
        <div className="ai-crew-labor-header">
          <span>Labor & Schedule Parameters</span>
          <span style={{ fontSize: 11, fontWeight: 500, color: '#94a3b8', textTransform: 'none' }}>
            Derived from request and precedent
          </span>
        </div>

        <div className="ai-crew-labor-grid">
          {laborFields.map((field) => (
            <div key={field.key} className="ai-crew-labor-card">
              <div className="ai-crew-labor-card-top">
                <span className="ai-crew-labor-dim-label">
                  {field.icon}
                  {field.label}
                </span>
              </div>
              <div className={`ai-crew-labor-dim-val ${!field.value ? 'unset' : ''}`}>
                {field.value || 'not set'}
              </div>
              <div className="ai-crew-labor-card-footer">
                <SourceBadge source={field.source} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
